// Build-time platform configuration - every value here defaults to PRI's own (YouTube/PIONEER)
// behaviour when its env var is unset, so an existing PRI deployment's build is byte-identical to
// today's without setting anything. A different deployment (e.g. Falcon/Pinterest) sets these as
// Docker build ARGs the same way VITE_AUTH0_* already are - see apps/web/Dockerfile, deploy.yml,
// deploy-env.sh.

export const PLATFORM_ID = import.meta.env.VITE_PLATFORM || 'youtube';

export const DEFAULT_AFFILIATE = import.meta.env.VITE_DEFAULT_AFFILIATE || 'PIONEER';

// The customer codename this deployment serves (always rendered upper case - PIONEER, FALCON,
// YUKON, ...), so the tab title and navbar make the deployment discernible at a glance rather than
// showing the generic product name every deployment shares.
export const PLATFORM_CODENAME = DEFAULT_AFFILIATE.toUpperCase();

// Whether this build is the customer's live environment. Only an explicit "live" counts - staging,
// local dev and a missing value are all "dev", so nothing can look live by accident.
export const IS_LIVE = import.meta.env.VITE_ENVIRONMENT_TYPE === 'live';

// The app switcher's id for PRI: one id on every deployment. A deployment is an org under it
// (see deploymentLabel), never an app of its own.
export const SWITCHER_APP_ID = 'pri';

// A deployment's own name, upper case: its host's first label (falcon-testing.pri.crispdev.com is
// FALCON-TESTING, falcon.pri.crispthinking.com is FALCON), or `fallback` for a host with no
// subdomain to take one from - localhost, an IP address.
export function deploymentLabel(hostname: string, fallback: string): string {
  const [first, ...rest] = hostname.split('.');
  return first !== undefined && rest.length > 1 && !/^\d+$/v.test(rest.at(-1) ?? '')
    ? first.toUpperCase()
    : fallback;
}

// Which non-live environment this build is, for the design system's EnvironmentBanner. Live has no
// banner (undefined); staging is its own colour, and everything else - dev, local, a missing value -
// reads as dev, mirroring IS_LIVE.
export const ENVIRONMENT_BANNER_ENV: 'staging' | 'dev' | undefined = IS_LIVE
  ? undefined
  : import.meta.env.VITE_ENVIRONMENT_TYPE === 'staging'
    ? 'staging'
    : 'dev';

// Where '/' lands, and where the in-shell 404 offers to take you.
export const DEFAULT_ROUTE = '/dashboard';

// Default spider_name for a new admin-created Scrapy Collection (ScrapyCollectionsAdmin, CSV
// import). Each deployment only ever targets one platform's spider, so this is a build-time
// constant rather than a user-editable field.
export const DEFAULT_SPIDER_NAME =
  PLATFORM_ID === 'pinterest' ? 'pinterest_profile' : 'youtube_search';

// Generic noun for one collected item, used in Investigations' item counts/banners/tooltips.
// YouTube's items genuinely are videos; Falcon's are a mix of profiles/pins/boards, so "item" is
// the honest generic term there rather than hardcoding "video" everywhere.
export const ITEM_NOUN = PLATFORM_ID === 'pinterest' ? 'item' : 'video';
export const ITEM_NOUN_PLURAL = PLATFORM_ID === 'pinterest' ? 'items' : 'videos';

// The platform's name, for copy that names where results come from ("Collecting 12 videos from YouTube").
export const PLATFORM_NAME = PLATFORM_ID === 'pinterest' ? 'Pinterest' : 'YouTube';

// Noun for an actor-level item (item.content_type === 'actor') - a YouTube channel, a Pinterest
// profile.
export const ACTOR_NOUN = PLATFORM_ID === 'pinterest' ? 'profile' : 'channel';

// Tooltip for the verified-identity flag. Pinterest also has a merchant badge, so say which one.
export const VERIFIED_LABEL =
  PLATFORM_ID === 'pinterest' ? 'Identity verified' : 'Channel verified';

// Capitalised forms for badges and headings.
export const ACTOR_LABEL = PLATFORM_ID === 'pinterest' ? 'Profile' : 'Channel';
export const CONTENT_LABEL = PLATFORM_ID === 'pinterest' ? 'Pin' : 'Video';

// The engagement count an item list shows - Pinterest has saves, not likes.
export const ENGAGEMENT_LABEL = PLATFORM_ID === 'pinterest' ? 'Saves' : 'Likes';
